import { useEffect, useRef, useState, useCallback } from 'react';
import { getUnixTime } from 'date-fns';
import { useNavigate } from '@tanstack/react-location';
import { weeks, formatDate } from '@/components/Common/utils';
import type {
  MonthCalendarPresenterPropsType,
  daysType,
} from '@/components/MonthCalendar/entities';
import { classNames, getAddDay, getIsHoliday } from '@/components/MonthCalendar/utils';
import {
  type DisplayNowDateType,
  type MonthDayButtonsType,
  type MoveDateButtonType,
  type MoveMonthButtonType,
  type MoveToNowDateButtonType,
} from '../../utils/components/parts';

// カレンダー関連のサブ関数
function useCalendarSetup(container: React.RefObject<HTMLDivElement>, containerNav: React.RefObject<HTMLDivElement>, containerOffset: React.RefObject<HTMLDivElement>): void {
  useEffect(() => {
    if (
      container.current != null &&
      containerNav.current != null &&
      containerOffset.current != null
    ) {
      // Set the container scroll position based on the current time.
      const currentMinute = new Date().getHours() * 60;
      container.current.scrollTop =
        ((container.current.scrollHeight -
          containerNav.current.offsetHeight -
          containerOffset.current.offsetHeight) *
          currentMinute) /
        1440;
    }
  }, [container, containerNav, containerOffset]);
}

// カレンダーの同期処理
function useSyncCalendar(nowDate: Date, holidays: any): void {
  const navigate = useNavigate();
  
  useEffect(() => {
    // URLパラメータの更新のみを行う
    const params = new URLSearchParams(location.search);
    params.set('calendar-date', getUnixTime(nowDate).toString());
    navigate({ to: `${location.pathname}?${params.toString()}`, fromCurrent: true });
  }, [nowDate, holidays, navigate]);
}

export function MonthCalendarPresenter ({ nowDate, setNowDate, holidays }: MonthCalendarPresenterPropsType): JSX.Element {
  const container = useRef<HTMLDivElement>(null);
  const containerNav = useRef<HTMLDivElement>(null);
  const containerOffset = useRef<HTMLDivElement>(null);
  const [days, setDays] = useState<daysType[]>();

  // カスタムフックを使用して処理を分割
  useCalendarSetup(container, containerNav, containerOffset);

  // 日付移動
  function calcDate (value: number): void {
    const _nowDate = new Date(nowDate.setDate(nowDate.getDate() + value));
    setNowDate(_nowDate);
  }

  // 月移動
  function calcMonth (value: number): void {
    // 明示的に年と月を計算
    let year = nowDate.getFullYear();
    let month = nowDate.getMonth() + value;
    
    // 月が0未満または11を超える場合に年を調整
    if (month < 0) {
      const yearsToSubtract = Math.floor((Math.abs(month) + 11) / 12);
      year -= yearsToSubtract;
      month = 12 - (Math.abs(month) % 12);
      if (month === 12) month = 0;
    } else if (month > 11) {
      const yearsToAdd = Math.floor(month / 12);
      year += yearsToAdd;
      month = month % 12;
    }
    
    // 常に新しい月の1日を指定して新しい日付オブジェクトを作成
    const _nowDate = new Date(year, month, 1);
    
    // デバッグ情報
    console.log('元の日付:', nowDate);
    console.log('調整後の年月:', year, month);
    console.log('新しい日付:', _nowDate);
    
    setNowDate(_nowDate);
  }

  // カレンダー作成関数を安定した参照に
  const createCalendarFn = useCallback(() => {
    const date = nowDate;
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const lastMonth = date.getMonth();
    const nextMonth = date.getMonth() + 2;
    
    const startDate = new Date(year, month - 1, 1); // 月の最初の日を取得
    const endDate = new Date(year, month, 0); // 月の最後の日を取得
    const endDayCount = endDate.getDate(); // 月の末日
    const lastMonthEndDate = new Date(year, month - 1, 0); // 前月の最後の日の情報
    const lastMonthendDayCount = lastMonthEndDate.getDate(); // 前月の末日
    const startDay = startDate.getDay(); // 月の最初の日の曜日を取得
    
    // 予約不可日数と定休日をoffにする値
    const limitCancellationNum: number = -1;
    const reservationNotPossibleDays: number = limitCancellationNum;
    const regularHoliday: number = limitCancellationNum;

    // 日付データを作成
    const calendarDays: daysType[] = [];
    
    // 先月の日程を追加
    if (startDay > 0) {
      calendarDays.push(...createPreviousMonthDays(year, lastMonth, startDay, lastMonthendDayCount));
    }
    
    // 今月の日程を追加
    calendarDays.push(...createCurrentMonthDays(
      year, month, endDayCount, nowDate, reservationNotPossibleDays, regularHoliday, holidays
    ));
    
    // 来月の日程を追加（必要な数だけ）
    const remainingCells = 42 - calendarDays.length; // 6行×7列=42 から既に追加した日付を引く
    if (remainingCells > 0) {
      const nextMonthDays = createNextMonthDays(year, nextMonth, endDayCount + 1, endDayCount).slice(0, remainingCells);
      calendarDays.push(...nextMonthDays);
    }
    
    setDays(calendarDays);
  }, [nowDate, holidays]);

  // カレンダー同期とカレンダー作成を別々に管理
  useSyncCalendar(nowDate, holidays);
  
  // カレンダーデータの作成
  useEffect(() => {
    createCalendarFn();
  }, [createCalendarFn]);

  return (
    <Outline>
      <MoveMonthButtonsOutline>
        <MoveToPrevMonthButton calcMonth={calcMonth} />
        <DisplayNowDate nowDate={nowDate} />
        <MoveToNextMonthButton calcMonth={calcMonth} />
      </MoveMonthButtonsOutline>

      <WeekDays />

      <MonthDayButtons days={days} setNowDate={setNowDate} />

      <MoveDateButtonsOutline>
        <MoveToPrevWeekButton calcDate={calcDate} />
        <MoveToPrevDayButton calcDate={calcDate} />
        <MoveToNowDateButton setNowDate={setNowDate} />
        <MoveToNextDayButton calcDate={calcDate} />
        <MoveToNextWeekButton calcDate={calcDate} />
      </MoveDateButtonsOutline>
    </Outline>
  );
}

// 先月の日程を作成するヘルパー関数
function createPreviousMonthDays(year: number, lastMonth: number, startDay: number, lastMonthendDayCount: number): daysType[] {
  const days: daysType[] = [];
  for (let dayBox = 0; dayBox < startDay; dayBox++) {
    const num = lastMonthendDayCount - startDay + dayBox + 1;
    days.push({
      date: `${year}/${lastMonth}/${num}`,
      isDisabled: true,
      isCurrentMonth: false,
      isHoliday: false,
    });
  }
  return days;
}

// 来月の日程を作成するヘルパー関数
function createNextMonthDays(year: number, nextMonth: number, dayCount: number, endDayCount: number): daysType[] {
  const days: daysType[] = [];
  let currentDayCount = dayCount;
  while (currentDayCount <= 42) { // 6行×7列=42
    const num = currentDayCount - endDayCount;
    days.push({
      date: `${year}/${nextMonth}/${num}`,
      isDisabled: true,
      isCurrentMonth: false,
      isHoliday: false,
    });
    currentDayCount++;
  }
  return days;
}

// 今月の日程を作成するヘルパー関数
function createCurrentMonthDays(
  year: number, 
  month: number, 
  endDayCount: number, 
  nowDate: Date,
  reservationNotPossibleDays: number,
  regularHoliday: number,
  holidays: any
): daysType[] {
  const days: daysType[] = [];
  for (let dayCount = 1; dayCount <= endDayCount; dayCount++) {
    const roopDate: Date = new Date(`${year}/${month}/${dayCount}`);
    days.push({
      date: `${year}/${month}/${dayCount}`,
      isDisabled:
        getAddDay(new Date(), reservationNotPossibleDays) < roopDate
          ? roopDate.getDay() === regularHoliday
          : true,
      isCurrentMonth: true,
      isSelected: new Date(nowDate).getDate() === dayCount,
      isToday: formatDate(new Date()) === formatDate(roopDate),
      isHoliday: roopDate.getDay() === 0 || getIsHoliday(roopDate, holidays),
    });
  }
  return days;
}

// NOTE 230529 Commonからインポートする（まだmainブランチに入ってない）
export interface OutlineType {
  children?: React.ReactNode;
}

const Outline: React.FC<OutlineType> = ({ children }) => {
  const outlineStyle = "h-1/2 gap-x-4 gap-y-2 text-sm overflow-y-scroll w-full flex-col flex box-border"
  const containerStyle = "hidden max-w-md flex-none md:block"
  return (
    <div className={outlineStyle}>
      {/* <p className="self-end text-gray-900 font-semibold">予約: {numberOfReservations} 件</p> */}
      <div className={containerStyle}>{children}</div>
    </div>
  );
};

const MoveMonthButtonsOutline: React.FC<OutlineType> = ({ children }) => {
  const outlineStyle = "grid grid-cols-5 items-center text-center text-gray-900"
  return <div className={outlineStyle}>{children}</div>;
};

const MoveToPrevMonthButton: React.FC<MoveMonthButtonType> = ({ calcMonth }) => {
  const buttonStyle = "text-white bg-gray-700 hover:bg-gray-800 font-medium rounded-l text-[0.7rem] h-7 text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
  return (
    <button
      onClick={() => { calcMonth(-1); }}
      type="button"
      className={buttonStyle}
    >
      先月
    </button>
  );
};

const DisplayNowDate: React.FC<DisplayNowDateType> = ({ nowDate }) => {
  const containerStyle = "col-span-3 text-[0.7rem] flex-auto font-semibold leading-3"
  return (
    <div className={containerStyle}>
      <span>{`${nowDate.getFullYear()}`}</span>
      <br />
      <span>{`${new Date(nowDate).getMonth() + 1}月${new Date(nowDate).getDate()}日(${weeks[nowDate.getDay()]
        })`}</span>
    </div>
  );
};

const MoveToNextMonthButton: React.FC<MoveMonthButtonType> = ({ calcMonth }) => {
  const buttonStyle = "text-white bg-gray-700 hover:bg-gray-800 font-medium rounded-r text-[0.7rem] h-7 text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
  return (
    <button
      onClick={() => { calcMonth(1); }}
      type="button"
      className={buttonStyle}
    >
      翌月
    </button>
  );
};

const WeekDays: React.FC = () => {
  const containerStyle = "mt-1 grid grid-cols-7 text-center text-xs leading-6 text-gray-500"
  return (
    <div className={containerStyle}>
      <div>日</div>
      <div>月</div>
      <div>火</div>
      <div>水</div>
      <div>木</div>
      <div>金</div>
      <div>土</div>
    </div>
  );
};

const MonthDayButtons: React.FC<MonthDayButtonsType> = ({ days, setNowDate }) => {
  const containerStyle = "isolate mt-1 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200"

  const createValidDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('/').map(Number);
    // JavaScriptの月は0から始まるため、monthから1を引く
    return new Date(year, month - 1, day);
  };

  return (
    <div className={containerStyle}>
      {days?.map((day, dayIdx) => (
        <button
          onClick={() => {
            if (day.isCurrentMonth ?? false) {
              setNowDate(createValidDate(day.date));
            } else {
              setNowDate(createValidDate(day.date));
            }
          }}
          value={day.date}
          key={day.date}
          type="button"
          className={classNames(
            'py-0.5 hover:bg-gray-100 focus:z-10',
            day.isDisabled && day.isCurrentMonth === false
              ? 'bg-gray-100'
              : day.isHoliday !== undefined && day.isHoliday
                ? 'bg-red-200'
                : 'bg-white',
            ((day.isSelected ?? false) || (day.isToday ?? false)) && 'font-semibold',
            (day.isSelected ?? false) && 'text-white',
            (day.isSelected === undefined || !day.isSelected) &&
            (day.isCurrentMonth ?? false) &&
            (day.isToday === undefined || !day.isToday) &&
            'text-gray-900',
            (day.isSelected === undefined || !day.isSelected) &&
            (day.isCurrentMonth === undefined || !day.isCurrentMonth) &&
            (day.isToday === undefined || !day.isToday) &&
            'text-gray-400',
            (day.isToday ?? false) &&
            (day.isSelected === undefined || !day.isSelected) &&
            'text-indigo-600',
            dayIdx === 0 && 'rounded-tl-lg',
            dayIdx === 6 && 'rounded-tr-lg',
            dayIdx === days.length - 7 && 'rounded-bl-lg',
            dayIdx === days.length - 1 && 'rounded-br-lg'
          )}
        >
          <time
            dateTime={day.date}
            className={classNames(
              'mx-auto flex h-7 w-7 items-center justify-center rounded-full',
              (day.isSelected ?? false) && (day.isToday ?? false) && 'bg-indigo-600',
              (day.isSelected ?? false) &&
              day.isToday !== undefined &&
              !day.isToday &&
              'bg-gray-900'
            )}
          >
            {day.date.split('/').pop()?.replace(/^0/, '')}
          </time>
        </button>
      ))}
    </div>
  );
};

const MoveDateButtonsOutline: React.FC<OutlineType> = ({ children }) => {
  const outlineStyle = "flex flex-row justify-between relative z-0 shadow-sm rounded-md w-full grid grid-cols-6 gap-0.5 h-8 mt-3"
  return (
    <div className={outlineStyle}>
      {children}
    </div>
  );
};

const MoveToPrevWeekButton: React.FC<MoveDateButtonType> = ({ calcDate }) => {
  const buttonStyle = "text-white bg-gray-700 hover:bg-gray-800 font-medium rounded-l text-[0.7rem] text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
  return (
    <button
      onClick={() => { calcDate(-7); }}
      type="button"
      className={buttonStyle}
    >
      先週
    </button>
  );
};

const MoveToPrevDayButton: React.FC<MoveDateButtonType> = ({ calcDate }) => {
  const buttonStyle = "text-white bg-gray-700 hover:bg-gray-800 font-medium text-[0.7rem] text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
  return (
    <button
      onClick={() => { calcDate(-1); }}
      type="button"
      className={buttonStyle}
    >
      前日
    </button>
  );
};

const MoveToNowDateButton: React.FC<MoveToNowDateButtonType> = ({ setNowDate }) => {
  const buttonStyle = "col-span-2 text-white bg-indigo-600 hover:bg-indigo-700 font-medium text-[0.7rem] text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
  return (
    <button
      onClick={() => {
        setNowDate(new Date());
      }}
      type="button"
      className={buttonStyle}
    >
      当日
    </button>
  );
};
const MoveToNextDayButton: React.FC<MoveDateButtonType> = ({ calcDate }) => {
  const buttonStyle = "text-white bg-gray-700 hover:bg-gray-800 font-medium text-[0.7rem] text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
  return (
    <button
      onClick={() => { calcDate(1); }}
      type="button"
      className={buttonStyle}
    >
      翌日
    </button>
  );
};

const MoveToNextWeekButton: React.FC<MoveDateButtonType> = ({ calcDate }) => {
  const buttonStyle = "text-white bg-gray-700 hover:bg-gray-800 font-medium rounded-r text-[0.7rem] text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
  return (
    <button
      onClick={() => { calcDate(7); }}
      type="button"
      className={buttonStyle}
    >
      翌週
    </button>
  );
};
